<template>
    <section class="viewer">
      <div v-if="!pdfLoaded" class="viewer-empty">
        <div class="big">上傳一份 NotebookLM 匯出的 PDF</div>
        <div class="small">按「開始分析」才會跑 OCR，並產生可編輯文字框</div>
      </div>
  
      <div v-else class="viewer-wrap">
        <div class="viewer-top">
          <div class="vt-left">
            <div class="doc-title">{{ docTitle }}</div>
            <div class="doc-sub">
              {{ currentPageNumber }} / {{ pagesLen }} ·
              {{ currentPage?.analyzed ? '已分析' : '未分析' }}
            </div>
          </div>
          <div class="vt-right">
            <button
              class="btn ghost"
              type="button"
              :disabled="!currentPage || currentPage.analyzing"
              @click="$emit('analyze', currentPageNumber)"
            >
              {{ currentPage?.analyzing ? '分析中…' : (currentPage?.analyzed ? '重新分析此頁' : '開始分析此頁') }}
            </button>
          </div>
        </div>
  
        <div class="canvas-area">
          <div class="busy" v-if="currentPage?.analyzing">
            <div class="busy-content">
              <div class="spinner"></div>
              <div class="txt">{{ busyText }}</div>
            </div>
          </div>
  
          <div class="page-layer">
            <div class="img-wrap">
              <img
                v-if="currentPage?.pageUrl"
                :key="`${currentPageNumber}-${currentPage?.renderRev ?? 0}`"
                class="page-img"
                :src="currentPage.pageUrl"
                alt="page"
                @load="onImgLoad"
              />
              <div class="overlay">
                <button
                  v-for="b in currentPageBlocks"
                  :key="b.id"
                  class="box"
                  type="button"
                  :data-bid="b.id"
                  :class="{ selected: b.id === selectedBlockId }"
                  :style="boxStyle(b)"
                  @click.stop="$emit('selectBlock', b.id)"
                  title="點選編輯"
                />
              </div>
            </div>
          </div>
        </div>
  
        <!-- mobile helper -->
        <div class="mobile-actions">
          <button class="btn ghost" type="button" @click="$emit('openMobileEditor')">
            📝 文字框 ({{ currentPageBlocks.length }})
          </button>
        </div>
      </div>
    </section>
  </template>
  
  <script setup lang="ts">
  import type { OcrBlock, PageState } from '@/types/ainotebook'
  import { nextTick } from 'vue'
  
  const props = defineProps<{
    pdfLoaded: boolean
    docTitle: string
    pagesLen: number
    currentPageNumber: number
    currentPage: PageState | null
    currentPageBlocks: OcrBlock[]
    selectedBlockId: string
    busyText: string
  }>()
  
  defineEmits<{
    (e: 'analyze', n: number): void
    (e: 'selectBlock', id: string): void
    (e: 'needMain', n: number): void
    (e: 'goPage', n: number): void
    (e: 'openMobileEditor'): void
  }>()
  
  async function onImgLoad() {
    await nextTick()
  }
  
  function boxStyle(b: OcrBlock) {
    const p = props.currentPage
    if (!p) return {}
    const x = (b.rect.x / p.width) * 100
    const y = (b.rect.y / p.height) * 100
    const w = (b.rect.w / p.width) * 100
    const h = (b.rect.h / p.height) * 100
    return { left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }
  }
  </script>
  
  <style scoped lang="scss">
  .viewer {
    min-height: 0;
    height: 100%;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 16px;
    background: rgba(10,18,33,0.55);
    backdrop-filter: blur(10px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .viewer-empty {
    flex: 1;
    display: grid;
    place-items: center;
    gap: 8px;
    text-align: center;
    padding: 20px;
  
    .big { font-weight: 800; font-size: 16px; }
    .small { font-size: 13px; color: rgba(232,238,252,0.65); max-width: 520px; }
  }
  
  .viewer-wrap {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .viewer-top {
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  
    .doc-title { font-weight: 800; }
    .doc-sub { font-size: 12px; color: rgba(232,238,252,0.65); margin-top: 4px; }
  }
  
  .canvas-area {
    position: relative;
    margin: 12px;
    border-radius: 16px;
    overflow: hidden;
    background: rgba(0,0,0,0.35);
    border: 1px solid rgba(255,255,255,0.06);
  
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 10px;
  }
  
  .page-layer {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
  }
  
  .img-wrap {
    position: relative;
    display: inline-block;
    max-width: 100%;
    max-height: 100%;
  }
  
  .page-img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    pointer-events: none;
    user-select: none;
  }
  
  .overlay {
    position: absolute;
    inset: 0;
  }
  
  .box {
    position: absolute;
    border: 2px solid rgba(102, 153, 255, 0.22);
    background: rgba(102, 153, 255, 0.10);
    border-radius: 2px;
    padding: 0;
    cursor: pointer;
    transition: 0.12s ease;
  
    &:hover { border-color: rgba(102, 153, 255, 0.55); background: rgba(102, 153, 255, 0.14); }
    &.selected { border-color: rgba(29,123,255,0.95); background: rgba(29,123,255,0.18); box-shadow: 0 0 0 4px rgba(29,123,255,0.15); }
  }
  
  .busy {
    position: absolute;
    inset: 0;
    z-index: 5;
  }
  .busy::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(5, 10, 18, 0.55);
    backdrop-filter: blur(6px);
  }
  .busy-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: grid;
    justify-items: center;
    gap: 10px;
    text-align: center;
  }
  .spinner {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    border: 3px solid rgba(255,255,255,0.18);
    border-top-color: rgba(29,123,255,0.95);
    animation: spin 0.8s linear infinite;
  }
  .txt { font-size: 13px; color: rgba(232,238,252,0.8); }
  @keyframes spin { to { transform: rotate(360deg); } }
  
  .btn {
    height: 40px;
    padding: 0 14px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.10);
    background: rgba(255,255,255,0.05);
    color: #e8eefc;
    cursor: pointer;
    font-weight: 700;
  }
  .btn.ghost { background: rgba(255,255,255,0.03); }
  
  .mobile-actions { display: none; }
  
  @media (max-width: 720px) {
    .viewer {
      border-radius: 0;
      border: none;
    }
    .viewer-top {
      padding: 8px;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
      .btn { width: 100%; height: 36px; font-size: 12px; }
    }
  
    .canvas-area {
      flex: 1;
      margin: 0;
      border-radius: 0;
      border: none;
      overflow: auto;
      padding: 0;
      -webkit-overflow-scrolling: touch;
    }
    .page-layer { min-width: 100%; min-height: 100%; padding: 8px; }
    .img-wrap { display: inline-block; }
    .page-img { width: auto; height: auto; }
  
    .mobile-actions {
      display: block;
      padding: 8px;
    }
  }
  </style>
  